import { ShoppingBag, Truck, Coins, Clock } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import VisionOverlay from './VisionOverlay';

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen
      bg-gradient-to-b from-neutral-50 via-white to-neutral-100 text-neutral-900
      dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 dark:text-neutral-100
      transition-colors"
    >
      {/* HERO */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] border border-gray-200/80 dark:border-white/10
                          bg-white/85 dark:bg-[#121b2e]/95 backdrop-blur-md
                          shadow-[0_10px_40px_rgba(0,0,0,.10)] dark:shadow-[0_10px_40px_rgba(0,0,0,.35)]
                          p-10 md:p-14 text-center">
            <img src="/bukken.png" alt="Væren" className="h-20 w-20 md:h-24 md:w-24 mx-auto mb-6" />

            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Dårligt vær?
            </h1>
            <p className="text-3xl md:text-5xl font-bold text-[#004D99] dark:text-orange-400 mt-2">
              Væren levere allti.
            </p>

            <p className="mt-6 text-lg md:text-xl text-gray-700 dark:text-white/75 max-w-2xl mx-auto">
              Din lokale mat- og dagligvareleveringstjeneste i Gjesdal. Rask levering, god mat, og støtte til lokale butikker.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/food')}
                className="px-8 py-4 rounded-xl bg-[#004D99] hover:bg-[#003366] text-white font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                Bestill Mat Nå
              </button>
              <button
                onClick={() => navigate('/drivers')}
                className="px-8 py-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                Bli Sjåfør
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SÅNN FUNGERER DET */}
      <section className="py-16 bg-white dark:bg-transparent transition-colors">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-center mb-10">Sånn fungerer det</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Feature
              Icon={ShoppingBag}
              title="Velg din mat"
              desc="Bla gjennom menyen fra våre partnere og velg det du vil ha."
              tone="blue"
            />
            <Feature
              Icon={Truck}
              title="Vi leverer raskt"
              desc="Våre lokale sjåfører leverer maten rett til døren din."
              tone="orange"
            />
            <Feature
              Icon={Coins}
              title="Tjen VærCoins"
              desc="Tjen 1% av ordretotalen og bruk dem på premier."
              tone="green"
            />
          </div>
        </div>
      </section>

      {/* LATE NIGHT */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl border border-orange-200/60 dark:border-white/10 bg-orange-50 dark:bg-white/5 p-8 text-center shadow-sm">
            <Clock className="w-10 h-10 text-orange-600 dark:text-orange-400 mx-auto mb-3" />
            <h3 className="text-xl font-semibold mb-1">Late-night Snacks</h3>
            <p className="text-gray-700 dark:text-white/75 mb-4">
              Vi leverer til 23:00 på hverdager og 01:00 i helgene.
            </p>
            <button
              onClick={() => navigate('/food')}
              className="px-6 py-3 rounded-xl bg-[#004D99] hover:bg-[#003366] text-white font-medium transition-colors active:scale-95"
            >
              Se nattmenyen
            </button>
          </div>
        </div>
      </section>

      {/* LEVERINGSSONER */}
      <section className="py-16 bg-white dark:bg-transparent transition-colors">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-center mb-10">Leveringssoner</h2>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <ZoneCard
              title="Sone 1 – Ålgård Sentrum"
              price="39 kr"
              desc="Innenfor 1 km radius fra Ålgård sentrum."
              tone="blue"
            />
            <ZoneCard
              title="Sone 2 – Utvidet Ålgård"
              price="49 kr"
              desc="Innenfor 3 km radius, men utenfor 1 km."
              tone="orange"
            />
            <ZoneCard
              title="Sone 3 – Resten av Gjesdal"
              price="79–99 kr"
              desc="Utenfor 3 km, fortsatt innenfor Gjesdal kommune."
              tone="green"
            />
          </div>

          {/* “Statisk” kart: deaktiver interaksjon, legg på knapp for interaktiv versjon */}
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f1724]">
            <div className="relative">
              <iframe
                style={{ overflow: 'hidden', width: '100%', height: '520px' }}
                allow="geolocation *; camera *;"
                frameBorder={0}
                src="https://www.mapotic.com/se-hvilke-omrader-vi-leverer-til/embed?zoom=10&lat=58.780271756731224&lng=6.071417668361889"
                title="Leveringskart"
                className="pointer-events-none select-none w-full h-[520px]"
              />
              <div className="absolute inset-x-0 bottom-0 p-4 flex justify-end">
                <a
                  href="https://www.mapotic.com/se-hvilke-omrader-vi-leverer-til"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl bg-white/90 backdrop-blur px-4 py-2 text-sm font-medium border border-gray-200 shadow hover:bg-white dark:bg-gray-900/90 dark:border-white/10 dark:hover:bg-gray-900"
                >
                  Åpne interaktivt kart
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-18 md:py-20 bg-gradient-to-b from-white to-neutral-100 dark:from-transparent dark:to-transparent transition-colors">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Klar til å bestille?</h2>
          <p className="text-lg md:text-xl text-gray-700 dark:text-white/75 mb-8 max-w-2xl mx-auto">
            Støtt lokale restauranter og få favorittmaten levert hjem.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/food')}
              className="px-10 py-4 rounded-xl bg-[#004D99] hover:bg-[#003366] text-white font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              Bestill nå
            </button>
            <Link
              to="/food"
              className="px-10 py-4 rounded-xl bg-white/85 dark:bg-white/10 hover:bg-white dark:hover:bg-white/15 text-gray-900 dark:text-white font-semibold border border-gray-200 dark:border-white/10 transition-all"
            >
              Se menyen
            </Link>
          </div>
        </div>
      </section>

      <VisionOverlay />
    </div>
  );
}

/* ——— små komponenter ——— */

function Feature({
  Icon,
  title,
  desc,
  tone = 'blue',
}: {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  tone?: 'blue' | 'orange' | 'green';
}) {
  const toneMap: Record<string, { wrap: string; icon: string }> = {
    blue: {
      wrap: 'bg-white dark:bg-gray-900 border-gray-200 dark:border-white/10',
      icon: 'bg-blue-50 dark:bg-white/10 text-[#004D99] dark:text-orange-400',
    },
    orange: {
      wrap: 'bg-white dark:bg-gray-900 border-gray-200 dark:border-white/10',
      icon: 'bg-orange-50 dark:bg-white/10 text-orange-600 dark:text-orange-400',
    },
    green: {
      wrap: 'bg-white dark:bg-gray-900 border-gray-200 dark:border-white/10',
      icon: 'bg-emerald-50 dark:bg-white/10 text-emerald-600 dark:text-emerald-400',
    },
  };

  return (
    <div className={`text-center rounded-2xl border ${toneMap[tone].wrap} p-6 shadow-sm`}>
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-3 ${toneMap[tone].icon}`}>
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-gray-600 dark:text-white/70 text-sm">{desc}</p>
    </div>
  );
}

function ZoneCard({
  title,
  price,
  desc,
  tone,
}: {
  title: string;
  price: string;
  desc: string;
  tone: 'blue' | 'orange' | 'green';
}) {
  const toneMap = {
    blue: {
      wrap: 'bg-blue-50 dark:bg-white/[0.04] border-blue-200 dark:border-white/10',
      title: 'text-[#004D99] dark:text-orange-400',
    },
    orange: {
      wrap: 'bg-orange-50 dark:bg-white/[0.04] border-orange-200 dark:border-white/10',
      title: 'text-orange-600 dark:text-orange-400',
    },
    green: {
      wrap: 'bg-emerald-50 dark:bg-white/[0.04] border-emerald-200 dark:border-white/10',
      title: 'text-emerald-600 dark:text-emerald-400',
    },
  }[tone];

  return (
    <div className={`rounded-2xl p-6 border ${toneMap.wrap} shadow-sm`}>
      <h3 className={`text-lg font-semibold mb-1 ${toneMap.title}`}>{title}</h3>
      <p className="text-3xl font-bold mb-1">{price}</p>
      <p className="text-gray-600 dark:text-white/70 text-sm">{desc}</p>
    </div>
  );
}
